import React, { useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import { BaseDialog, Button, DeleteIcon, DragHandleIcon, InfoIcon, TextField, Tooltip, Typography, UploadIcon, useConfirmation } from '@coolremote/shared';
import {
  IModbusDeviceRow,
  UI_TYPE_BINARY,
  UI_TYPE_ENUMERATION,
  UI_TYPE_REVERSE_BINARY,
  UI_TYPE_VALUE,
} from '../types';
import styles from './UITemplateMapper.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UIControlType = 'text' | 'onoff' | 'toggle' | 'dropdown' | 'updown';
export type UILabelPosition = 'left' | 'right' | 'top' | 'bottom';
export type UILabelSize = 'xs' | 'sm' | 'md' | 'lg';

export interface IUIControlPoint {
  id: string;
  rowIndex: number;
  labelName: string;
  labelPosition: UILabelPosition;
  labelSize: UILabelSize;
  textColor: string;
  controlType: UIControlType;
  x: number; // 0–100, percentage of canvas width
  y: number; // 0–100, percentage of canvas height
}

export interface IUITemplate {
  name: string;
  backgroundImage?: string; // base64 data URI
  points: IUIControlPoint[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TEXT_COLORS = [
  '#e91e63',
  '#f44336',
  '#ff9800',
  '#ffc107',
  '#8bc34a',
  '#4caf50',
  '#00bcd4',
  '#2196f3',
  '#7c4dff',
  '#9e9e9e',
  '#ffffff',
  '#212121',
];

const LABEL_FONT_SIZES: Record<UILabelSize, number> = {
  xs: 10,
  sm: 12,
  md: 15,
  lg: 19,
};

// Which Control Type widgets make sense for a given parameter, based on its
// register type (Binary/Reverse Binary/Enumeration/Value) and read-only state —
// mirrors the BACnet control mapping (BV/MV/AI/AV).
const allowedControlTypes = (row?: IModbusDeviceRow): UIControlType[] => {
  if (!row) {
    return ['text', 'onoff', 'toggle', 'dropdown', 'updown'];
  }
  switch (row.uiType) {
    case UI_TYPE_BINARY:
    case UI_TYPE_REVERSE_BINARY:
      return row.readOnly ? ['text'] : ['text', 'onoff', 'toggle'];
    case UI_TYPE_ENUMERATION:
      return ['text', 'dropdown'];
    case UI_TYPE_VALUE:
      return row.readOnly ? ['text'] : ['text', 'updown'];
    default:
      return ['text', 'onoff', 'toggle', 'dropdown', 'updown'];
  }
};

let _ptCounter = 0;
const nextPointId = () => `pt-${++_ptCounter}`;

// Loaded templates carry point ids from a previous session; the module counter
// resets to 0 on page load, so a freshly-added point can reuse a loaded id and
// both points end up sharing an id (dragging one moves both, then locks).
// Remapping on load guarantees every id comes from the same monotonic counter.
const withFreshIds = (points?: IUIControlPoint[]): IUIControlPoint[] =>
  (points ?? []).map((p) => ({ ...p, id: nextPointId() }));

const newPoint = (
  rowIndex: number,
  row: IModbusDeviceRow,
  x: number,
  y: number
): IUIControlPoint => ({
  id: nextPointId(),
  rowIndex,
  labelName: row.name,
  labelPosition: 'left',
  labelSize: 'sm',
  textColor: '#212121',
  controlType: 'text',
  x,
  y,
});

// ─── Widget overlay ───────────────────────────────────────────────────────────

interface IWidgetProps {
  point: IUIControlPoint;
  row: IModbusDeviceRow;
  number: number;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDragStart: (e: React.MouseEvent) => void;
}

const ControlWidget: React.FC<IWidgetProps> = ({
  point,
  row,
  number,
  isSelected,
  onSelect,
  onRemove,
  onDragStart,
}) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect();
    onDragStart(e);
  };

  const valueUI = () => {
    switch (point.controlType) {
      case 'onoff':
        return <button className={styles.widgetOnOff}>⏻</button>;
      case 'toggle':
        return (
          <div className={styles.widgetToggle}>
            <div className={styles.widgetToggleThumb} />
          </div>
        );
      case 'dropdown':
        return (
          <div className={styles.widgetDropdown}>
            Auto <span>∨</span>
          </div>
        );
      case 'updown':
        return (
          <div className={styles.widgetUpDown}>
            <span>∨</span>
            <span className={styles.widgetUpDownVal}>0</span>
            <span>∧</span>
          </div>
        );
      default:
        return (
          <span
            className={styles.widgetValue}
            style={{
              color: point.textColor,
              fontSize: LABEL_FONT_SIZES[point.labelSize],
            }}
          >
            —
          </span>
        );
    }
  };

  const label = (
    <span
      className={styles.widgetLabel}
      style={{
        color: point.textColor,
        fontSize: LABEL_FONT_SIZES[point.labelSize],
      }}
    >
      {point.labelName}
    </span>
  );

  const body = () => {
    const val = valueUI();
    switch (point.labelPosition) {
      case 'top':
        return (
          <>
            <div>{label}</div>
            <div>{val}</div>
          </>
        );
      case 'bottom':
        return (
          <>
            <div>{val}</div>
            <div>{label}</div>
          </>
        );
      case 'right':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {val}
            {label}
          </div>
        );
      default:
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {label}
            {val}
          </div>
        );
    }
  };

  return (
    <div
      className={`${styles.widget} ${isSelected ? styles.widgetSelected : ''}`}
      style={{ left: `${point.x}%`, top: `${point.y}%` }}
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
    >
      {body()}
      {isSelected && (
        <button
          className={styles.widgetRemove}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

// ─── Config panel ─────────────────────────────────────────────────────────────

interface IConfigPanelProps {
  point: IUIControlPoint;
  row?: IModbusDeviceRow;
  onChange: (patch: Partial<IUIControlPoint>) => void;
}

const ConfigPanel: React.FC<IConfigPanelProps> = ({ point, row, onChange }) => {
  const allowed = allowedControlTypes(row);

  // A point placed before its parameter's type/read-only state was set (or
  // loaded from an older template) may carry a controlType no longer valid
  // for that parameter — fall back to the first allowed option.
  useEffect(() => {
    if (!allowed.includes(point.controlType)) {
      onChange({ controlType: allowed[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point.id, row?.uiType, row?.readOnly]);

  return (
    <div className={styles.configContent}>
      <div className={styles.configField}>
        <Tooltip title={t`This is the label name that will be shown on the Remote Control Interface.`}>
          <label className={styles.configFieldLabel}>
            {t`Label Name`}
          </label>
        </Tooltip>
        <TextField
          fullWidth
          value={point.labelName}
          onChange={(v: string) => onChange({ labelName: v })}
        />
      </div>

      <div className={styles.configField}>
        <label className={styles.configFieldLabel}>{t`Label Position`}</label>
        <select
          className={styles.configSelect}
          value={point.labelPosition}
          onChange={(e) =>
            onChange({ labelPosition: e.target.value as UILabelPosition })
          }
        >
          <option value="left">{t`Left`}</option>
          <option value="right">{t`Right`}</option>
          <option value="top">{t`Top`}</option>
          <option value="bottom">{t`Bottom`}</option>
        </select>
      </div>

      <div className={styles.configField}>
        <label className={styles.configFieldLabel}>{t`Label Size`}</label>
        <div className={styles.sizeBtns}>
          {(['xs', 'sm', 'md', 'lg'] as UILabelSize[]).map((s, i) => (
            <button
              key={s}
              className={`${styles.sizeBtn} ${point.labelSize === s ? styles.sizeBtnActive : ''
                }`}
              style={{ fontSize: [10, 12, 15, 18][i] }}
              onClick={() => onChange({ labelSize: s })}
            >
              A
            </button>
          ))}
        </div>
      </div>

      <div className={styles.configField}>
        <label className={styles.configFieldLabel}>{t`Text Color`}</label>
        <div className={styles.colorPalette}>
          {TEXT_COLORS.map((c) => (
            <button
              key={c}
              className={`${styles.colorSwatch} ${point.textColor === c ? styles.colorSwatchActive : ''
                }`}
              style={{
                background: c,
                boxShadow:
                  c === '#ffffff' && point.textColor !== c
                    ? 'inset 0 0 0 1px #d5d2d5'
                    : undefined,
              }}
              onClick={() => onChange({ textColor: c })}
            />
          ))}
        </div>
      </div>

      <div className={styles.configField}>
        <label className={styles.configFieldLabel}>{t`Control Type`}</label>
        <div className={styles.radioGroup}>
          {(
            [
              ['text', t`Textual Value`, null],
              ['onoff', t`On/Off`, <span className={styles.ctIconOnOff}>⏻</span>],
              [
                'toggle',
                t`Toggle`,
                <span className={styles.ctIconToggle}>
                  <span className={styles.ctIconToggleThumb} />
                </span>,
              ],
              [
                'dropdown',
                t`Dropdown Menu`,
                <span className={styles.ctIconDropdown}>Auto ∨</span>,
              ],
              [
                'updown',
                t`Up/Down Arrows`,
                <span className={styles.ctIconUpDown}>
                  <span>∨</span>
                  <span>∧</span>
                </span>,
              ],
            ] as [UIControlType, string, React.ReactNode][]
          )
            .filter(([val]) => allowed.includes(val))
            .map(([val, label, icon]) => (
              <label key={val} className={styles.radioLabel}>
                <input
                  type="radio"
                  name="controlType"
                  checked={point.controlType === val}
                  onChange={() => onChange({ controlType: val })}
                />
                <span style={{ flex: 1 }}>{label}</span>
                {icon}
              </label>
            ))}
        </div>
      </div>
    </div>
  );
};

// ─── Main dialog ──────────────────────────────────────────────────────────────

interface IProps {
  open: boolean;
  rows: IModbusDeviceRow[];
  initialTemplate?: IUITemplate;
  /** Control UI names already used on this site — saving a duplicate is blocked. */
  existingNames?: string[];
  onClose: () => void;
  onSave: (template: IUITemplate) => void;
}

const UITemplateMapperDialog: React.FC<IProps> = ({
  open,
  rows,
  initialTemplate,
  existingNames,
  onClose,
  onSave,
}) => {
  const [templateName, setTemplateName] = useState(initialTemplate?.name ?? '');
  const [nameSubmitted, setNameSubmitted] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(
    initialTemplate?.backgroundImage ?? null
  );
  const [points, setPoints] = useState<IUIControlPoint[]>(
    withFreshIds(initialTemplate?.points)
  );
  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);
  const [dragRowIndex, setDragRowIndex] = useState<number | null>(null);
  const confirm = useConfirmation();

  useEffect(() => {
    if (open) {
      setTemplateName(initialTemplate?.name ?? '');
      setNameSubmitted(false);
      setBackgroundImage(initialTemplate?.backgroundImage ?? null);
      setPoints(withFreshIds(initialTemplate?.points));
      setSelectedPointId(null);
      setDragRowIndex(null);
      isDragging.current = false;
      dragStateRef.current = null;
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const canvasRef = useRef<HTMLDivElement>(null);
  const imageFileRef = useRef<HTMLInputElement>(null);
  const isDragging = useRef(false);
  const dragStateRef = useRef<{
    id: string;
    mx: number;
    my: number;
    px: number;
    py: number;
  } | null>(null);

  const placedRowIndices = new Set(points.map((p) => p.rowIndex));
  const selectedPoint = points.find((p) => p.id === selectedPointId) ?? null;

  const updatePoint = (patch: Partial<IUIControlPoint>) =>
    setPoints((prev) =>
      prev.map((p) => (p.id === selectedPointId ? { ...p, ...patch } : p))
    );

  const removePoint = (id: string) => {
    setPoints((prev) => prev.filter((p) => p.id !== id));
    if (selectedPointId === id) setSelectedPointId(null);
  };

  const movePoint = (id: string, x: number, y: number) =>
    setPoints((prev) => prev.map((p) => (p.id === id ? { ...p, x, y } : p)));

  const handleWidgetDragStart = (
    e: React.MouseEvent,
    point: IUIControlPoint
  ) => {
    if (isDragging.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    isDragging.current = true;
    dragStateRef.current = {
      id: point.id,
      mx: e.clientX,
      my: e.clientY,
      px: point.x,
      py: point.y,
    };
    const rect = canvas.getBoundingClientRect();

    const onMouseMove = (me: MouseEvent) => {
      const ds = dragStateRef.current;
      if (!ds) return;
      const dx = ((me.clientX - ds.mx) / rect.width) * 100;
      const dy = ((me.clientY - ds.my) / rect.height) * 100;
      movePoint(
        ds.id,
        Math.max(0, Math.min(100, ds.px + dx)),
        Math.max(0, Math.min(100, ds.py + dy))
      );
    };
    const onMouseUp = () => {
      isDragging.current = false;
      dragStateRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const loadImageFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => setBackgroundImage(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = async () => {
    const ok = await confirm({
      title: t`Remove Background Image`,
      bodyMessage: t`Removing the image will also reset all mapped parameters. Continue?`,
      confirmText: t`Remove`,
      cancelText: t`Cancel`,
    });

    if (!ok) {
      return;
    }

    setBackgroundImage(null);
    setPoints([]);
    setSelectedPointId(null);
    setDragRowIndex(null);
  };

  const handleCanvasDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Image file drop
    if (e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        loadImageFile(file);
        return;
      }
    }

    // Parameter drop onto canvas — requires image to be set first
    if (!backgroundImage || dragRowIndex == null) return;
    if (placedRowIndices.has(dragRowIndex)) return;
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(
      5,
      Math.min(95, ((e.clientX - rect.left) / rect.width) * 100)
    );
    const y = Math.max(
      5,
      Math.min(95, ((e.clientY - rect.top) / rect.height) * 100)
    );
    const row = rows[dragRowIndex];
    if (!row) return;
    const pt = newPoint(dragRowIndex, row, x, y);
    setPoints((prev) => [...prev, pt]);
    setSelectedPointId(pt.id);
    setDragRowIndex(null);
  };

  // A name unchanged from the template being edited isn't a conflict with itself.
  const isDuplicateName = (name: string) => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) {
      return false;
    }
    if (initialTemplate?.name.trim().toLowerCase() === trimmed) {
      return false;
    }
    return (existingNames ?? []).some((n) => n.trim().toLowerCase() === trimmed);
  };

  const handleSave = () => {
    setNameSubmitted(true);
    if (!templateName.trim() || isDuplicateName(templateName)) {
      return;
    }
    onSave({
      name: templateName.trim(),
      backgroundImage: backgroundImage ?? undefined,
      points,
    });
    onClose();
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={t`Map Parameters to Control UI Points`}
      onConfirm={handleSave}
      confirmLabel={t`Save`}
      cancelLabel={t`Cancel`}
      size="full"
      staticBackdrop
      style={{ maxWidth: 1045 }}
      containerClassName={styles.dialogContent}
    >
      <div className={styles.container}>
        {/* ── Left panel ── */}
        <div className={styles.leftPanel}>
          <TextField
            label={t`Control UI name`}
            fullWidth
            value={templateName}
            onChange={(v: string) => setTemplateName(v)}
            error={
              nameSubmitted && !templateName.trim()
                ? t`Required`
                : nameSubmitted && isDuplicateName(templateName)
                  ? t`A Control UI with this name already exists`
                  : undefined
            }
          />
          <Typography variant="sectionTitle">{t`Parameter List`}</Typography>
          <div className={styles.paramListWrapper}>
            <ul className={styles.paramList}>
              {rows.map((row, i) => (
                <li
                  key={i}
                  className={[
                    styles.paramItem,
                    !backgroundImage ? styles.paramItemDisabled : '',
                    placedRowIndices.has(i) ? styles.paramItemPlaced : '',
                    dragRowIndex === i ? styles.paramItemDragging : '',
                  ].join(' ')}
                  draggable={!!backgroundImage && !placedRowIndices.has(i)}
                  onDragStart={() =>
                    backgroundImage && !placedRowIndices.has(i) && setDragRowIndex(i)
                  }
                  onDragEnd={() => setDragRowIndex(null)}
                  onClick={() => {
                    if (placedRowIndices.has(i)) {
                      const pt = points.find((p) => p.rowIndex === i);
                      if (pt) setSelectedPointId(pt.id);
                    }
                  }}
                >
                  <DragHandleIcon width={4} height={16} color="#bbb" />
                  <span className={styles.paramName}>{row.name}</span>
                  {placedRowIndices.has(i) && (
                    <div
                      className={styles.paramMapped}
                      onClick={(e) => {
                        e.stopPropagation();
                        const pt = points.find((p) => p.rowIndex === i);
                        if (pt) setSelectedPointId(pt.id);
                      }}
                    >
                      <span className={styles.paramMappedCheck}>✓</span>
                      <span className={styles.paramMappedLabel}>{t`Mapped`}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── Center panel ── */}
        <div className={styles.centerPanel}>
          <Typography
            className={styles.canvasTitle}
          >{t`Control Screen`}</Typography>
          <div
            ref={canvasRef}
            className={styles.canvas}
            onDragOver={handleCanvasDragOver}
            onDrop={handleCanvasDrop}
            onClick={() => setSelectedPointId(null)}
          >
            {!backgroundImage ? (
              <div className={styles.canvasDropBox}>
                <UploadIcon width={52} height={42} color="#bbb" />
                <p className={styles.uploadText}>
                  {t`No background image set.`}
                  <br />
                  {t`Drag and drop files or upload from your computer to begin mapping parameters.`}
                </p>
                <Button
                  white
                  size="small"
                  onClick={() => imageFileRef.current?.click()}
                >
                  {t`Choose file`}
                </Button>
              </div>
            ) : (
              <>
                <img src={backgroundImage} className={styles.bgImage} alt="" />
                <button
                  type="button"
                  className={styles.deleteImageBtn}
                  title={t`Remove background image`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                >
                  <DeleteIcon width={16} height={16} color="#4b1c46" />
                </button>
                {points.map((pt, idx) => (
                  <ControlWidget
                    key={pt.id}
                    point={pt}
                    row={rows[pt.rowIndex]}
                    number={idx + 1}
                    isSelected={selectedPointId === pt.id}
                    onSelect={() => setSelectedPointId(pt.id)}
                    onRemove={() => removePoint(pt.id)}
                    onDragStart={(e) => handleWidgetDragStart(e, pt)}
                  />
                ))}
              </>
            )}
          </div>
          <input
            ref={imageFileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) loadImageFile(f);
              e.target.value = '';
            }}
          />
        </div>

        {/* ── Right panel ── */}
        <div className={styles.rightPanel}>
          <div className={styles.configHeader}>
            <Typography variant="optionTitle">{t`Control Configuration`}</Typography>
            <Typography variant="meta">{t`Select a parameter for settings.`}</Typography>
          </div>
          {selectedPoint && (
            <ConfigPanel
              point={selectedPoint}
              row={rows[selectedPoint.rowIndex]}
              onChange={updatePoint}
            />
          )}
        </div>
      </div>
    </BaseDialog>
  );
};

export default UITemplateMapperDialog;
export { UITemplateMapperDialog };
